import { KeyboardEvent, useMemo } from 'react';
import { Dice6 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Table } from '@Components';
import { ChainAvatar } from '@Components/ConnectedChainCard';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@Components/ui-v2/empty';
import { FieldServiceListItem } from '@Models';
import {
  assignFieldServiceColors,
  FIELD_SERVICE_AVATAR_TEXT_COLOR,
  getFieldServiceInitials,
} from './assignFieldServiceColor';
import {
  getRetailerName,
  isKnownTaskType,
  PAGE_HEADER_BAND_CLASS_NAME,
  TABLE_HEADER_CELL_CLASS_NAME,
  TABLE_SURFACE_CLASS_NAME,
} from './constants';

interface FieldServicesProps {
  fieldServices: FieldServiceListItem[];
}

const FieldServices = ({ fieldServices }: FieldServicesProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const colorsById = useMemo(() => assignFieldServiceColors(fieldServices.map((fs) => fs.id)), [fieldServices]);

  const columns = [
    {
      Header: t('FieldService.List.FieldService'),
      accessor: 'name',
      width: 'w-[35%]',
      Cell: ({ row }: { row: { original: FieldServiceListItem } }) => {
        const fieldService = row.original;
        return (
          <div className="flex items-center gap-3">
            <ChainAvatar
              size="sm"
              initials={getFieldServiceInitials(fieldService.name)}
              color={colorsById[fieldService.id]}
              textColor={FIELD_SERVICE_AVATAR_TEXT_COLOR}
            />
            <div className="flex flex-col">
              <span className="font-semibold text-gray-900">{fieldService.name}</span>
              {/* A firm can have more than one contract with this client, one per retailer --
                  this is what tells apart two rows that otherwise look identical. */}
              <span className="text-xs text-gray-500">{getRetailerName(fieldService.retailer)}</span>
            </div>
          </div>
        );
      },
    },
    {
      Header: t('FieldService.List.Stores'),
      accessor: 'stores_count',
      width: 'w-[20%]',
      Cell: ({ row }: { row: { original: FieldServiceListItem } }) => (
        <span className="tabular-nums font-semibold text-gray-900">
          {t('FieldService.List.StoresCount', { count: row.original.stores_count })}
        </span>
      ),
    },
    {
      Header: t('FieldService.List.TaskTypes'),
      accessor: 'approved_task_types',
      width: 'w-[45%]',
      Cell: ({ row }: { row: { original: FieldServiceListItem } }) =>
        row.original.approved_task_types.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {row.original.approved_task_types.map((taskType) => (
              <span
                key={taskType}
                className="rounded-[5px] border border-gray-200 bg-gray-50 px-[7px] py-0.5 text-[10.5px] font-semibold text-gray-500">
                {isKnownTaskType(taskType) ? t(`FieldService.TaskTypeShort.${taskType}`) : taskType}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        ),
    },
  ];

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <div className={PAGE_HEADER_BAND_CLASS_NAME}>
        <h1 className="truncate text-xl font-bold leading-8 text-gray-900">{t('FieldService.Title')}</h1>
      </div>

      <div className="w-full px-4 py-6 sm:px-7">
        <div className={TABLE_SURFACE_CLASS_NAME}>
          {fieldServices.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Dice6 strokeWidth={1.75} />
                </EmptyMedia>
                <EmptyTitle className="text-base font-semibold text-gray-900">
                  {t('FieldService.List.EmptyTitle')}
                </EmptyTitle>
                <EmptyDescription>{t('FieldService.List.EmptyDescription')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <Table
              columns={columns}
              data={fieldServices}
              pageSize={10}
              pagination={fieldServices.length > 10}
              headerCellClassName={TABLE_HEADER_CELL_CLASS_NAME}
              getRowClassName={() => 'cursor-pointer'}
              getRowProps={(row: { original: FieldServiceListItem }) => {
                const openDetail = () =>
                  navigate(`/field-services/${row.original.id}?retailer=${encodeURIComponent(row.original.retailer)}`);
                return {
                  tabIndex: 0,
                  onClick: openDetail,
                  onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openDetail();
                    }
                  },
                };
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FieldServices;
